import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

const useDebouncedValue = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const normalizeOptionValue = (v) => {
  if (v === null || v === undefined) return String(v);
  return String(v).trim();
};

const arraysEqualNormalized = (a, b) => {
  if (a.length !== b.length) return false;
  const aNormalized = a.map(normalizeOptionValue).sort();
  const bNormalized = b.map(normalizeOptionValue).sort();
  return JSON.stringify(aNormalized) === JSON.stringify(bNormalized);
};

const PostAnalysisMultiSelectDropdown = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Select options",
  width = "min-w-[200px]",
  enableSearch = false,
  maxDisplayItems = 2,
  allowClearAll = false,
  searchPlaceholder = "Search options...",
  maxHeight = "max-h-[200px]",
  applyOnClose = false, // New prop: if true, only apply changes when dropdown closes
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [localValue, setLocalValue] = useState(value); // Local state for batching selections
  const inputRef = useRef(null);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  const extractWidthValue = (widthClass) => {
    if (widthClass === "w-full") return "100%";
    if (widthClass === "min-w-[200px]") return "200px";
    if (widthClass === "min-w-[300px]") return "300px";
    const match = widthClass.match(/w-\[(\d+)px\]/);
    return match ? `${match[1]}px` : null;
  };
  const cssWidth = extractWidthValue(width) || "200px";

  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return typeof options[0] === "object"
      ? options
      : options.map((item) => ({ label: item, value: item }));
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!enableSearch || !debouncedSearch) return normalizedOptions;
    return normalizedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [debouncedSearch, normalizedOptions, enableSearch]);

  // Sync local value with prop value when it changes externally (only when dropdown is closed)
  useEffect(() => {
    if (!open || !applyOnClose) {
      setLocalValue(value);
    }
  }, [value, open, applyOnClose]);

  // Initialize local value when dropdown opens (if applyOnClose is true)
  useEffect(() => {
    if (open && applyOnClose) {
      // When dropdown opens, initialize local value from prop
      setLocalValue(value);
    }
  }, [open, applyOnClose, value]); // Initialize with current value when opening

  // Apply local changes when dropdown closes (if applyOnClose is true)
  useEffect(() => {
    if (!open && applyOnClose) {
      // Only apply if local value is different from current value
      const currentValue = Array.isArray(value) ? value : (value ? [value] : []);
      const localValueArray = Array.isArray(localValue) ? localValue : (localValue ? [localValue] : []);

      if (!arraysEqualNormalized(currentValue, localValueArray)) {
        onChange(localValueArray);
      }
    }
  }, [open, applyOnClose, localValue, value, onChange]);

  const toggleSelect = (val) => {
    // Use local value if applyOnClose is true, otherwise use prop value
    const workingValue = applyOnClose ? localValue : value;
    const currentValue = Array.isArray(workingValue) ? workingValue : (workingValue ? [workingValue] : []);

    const normalizedVal = normalizeOptionValue(val);

    // Check if value is already selected (use normalized comparison)
    const isSelected = currentValue.some(v => normalizeOptionValue(v) === normalizedVal);
    
    let newValue;
    if (isSelected) {
      // Remove from selection - use normalized comparison
      newValue = currentValue.filter((v) => normalizeOptionValue(v) !== normalizedVal);
    } else {
      // Add to selection - always use the value from options to ensure consistency
      // This ensures we use the exact format that's in the options array
      const matchingOption = normalizedOptions.find(opt => normalizeOptionValue(opt.value) === normalizedVal);
      const valueToAdd = matchingOption ? matchingOption.value : val;
      
      // Prevent duplicates (shouldn't happen, but just in case)
      const alreadyExists = currentValue.some(v => normalizeOptionValue(v) === normalizedVal);
      if (!alreadyExists) {
        newValue = [...currentValue, valueToAdd];
      } else {
        newValue = currentValue;
      }
    }
    
    if (applyOnClose) {
      // Update local state only
      setLocalValue(newValue);
    } else {
      // Apply immediately
      onChange(newValue);
    }
  };

  const clearAll = () => {
    if (applyOnClose) {
      setLocalValue([]);
    } else {
    onChange([]);
    }
  };

  // Use local value for display if applyOnClose is true and dropdown is open
  const displayValueSource = applyOnClose && open ? localValue : value;

  const displayValue = useMemo(() => {
    const valueToDisplay = displayValueSource;
    if (!valueToDisplay || (Array.isArray(valueToDisplay) && valueToDisplay.length === 0)) return placeholder;
    if (!Array.isArray(valueToDisplay)) {
      return normalizedOptions.find((o) => o.value === valueToDisplay)?.label || placeholder;
    }

    const labels = valueToDisplay
      .map((v) => {
        const normalizedV = normalizeOptionValue(v);
        const option = normalizedOptions.find((o) => normalizeOptionValue(o.value) === normalizedV);
        return option?.label;
      })
      .filter(Boolean);
    if (labels.length === 0) return placeholder;
    if (labels.length <= maxDisplayItems) return labels.join(', ');
    const displayedLabels = labels.slice(0, maxDisplayItems);
    const remainingCount = labels.length - maxDisplayItems;
    return `${displayedLabels.join(', ')} +${remainingCount} more`;
  }, [displayValueSource, normalizedOptions, placeholder, maxDisplayItems, open]);

  const isPlaceholder = !displayValueSource || (Array.isArray(displayValueSource) && displayValueSource.length === 0) || displayValue === placeholder;

  useEffect(() => {
    if (open && enableSearch && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open, enableSearch]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const selectedCount = Array.isArray(displayValueSource) ? displayValueSource.length : (displayValueSource ? 1 : 0);

  return (
    <div className="flex flex-col min-w-[200px]">
      {label && (
        <label className="text-sm font-medium text-foreground leading-none mb-1">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn("justify-between min-w-[200px] bg-background border border-border font-normal", width)}
            style={{ maxWidth: "100%" }}
          >
            <span 
              className={cn("truncate flex-1 text-left", isPlaceholder ? "text-muted-foreground" : "text-foreground")}
              title={(() => {
                const valueToDisplay = displayValueSource;
                if (Array.isArray(valueToDisplay) && valueToDisplay.length > 0) {
                  return valueToDisplay
                    .map((v) => {
                      const normalizedV = normalizeOptionValue(v);
                      const option = normalizedOptions.find((o) => normalizeOptionValue(o.value) === normalizedV);
                      return option?.label || v;
                    })
                    .join(', ');
                }
                return displayValue;
              })()}
            >
              {displayValue}
            </span>
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              {allowClearAll && selectedCount > 0 && (
                <X
                  className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                />
              )}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          style={{ width: cssWidth, maxWidth: "90vw" }}
          align="start"
          sideOffset={4}
        >
          {enableSearch && (
            <div
              className="px-3 py-2 border-b bg-background sticky top-0 z-10"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => {
                if (e.target.tagName !== "INPUT") e.preventDefault();
              }}
            >
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={inputRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-8 h-9 text-sm"
                  onKeyDown={(e) => {
                    if (["Escape", "ArrowUp", "ArrowDown", "Enter", " "].includes(e.key)) {
                      e.stopPropagation();
                    }
                  }}
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          <div className="p-2">
            {allowClearAll && selectedCount > 0 && (
              <div
                onClick={clearAll}
                className="flex items-center px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-muted text-destructive mb-1"
              >
                <X className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Clear all ({selectedCount})</span>
              </div>
            )}
            <div className={cn("space-y-1 overflow-auto", maxHeight)}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  // Use local value if applyOnClose is true, otherwise use prop value
                  const valueForSelection = applyOnClose ? localValue : value;
                  const currentValue = Array.isArray(valueForSelection) ? valueForSelection : (valueForSelection ? [valueForSelection] : []);
                  const selected = currentValue.some(v => {
                    const normalizedV = normalizeOptionValue(v);
                    const normalizedOpt = normalizeOptionValue(opt.value);
                    return normalizedV === normalizedOpt;
                  });
                  return (
                    <div
                      key={opt.value}
                      onClick={() => toggleSelect(opt.value)}
                      className={cn(
                        "flex items-center px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-muted transition-colors",
                        selected && "bg-muted"
                      )}
                    >
                      <div
                        className={cn(
                          "h-4 w-4 mr-2 rounded border border-gray-400 flex-shrink-0 transition-colors",
                          selected && "bg-primary border-primary"
                        )}
                      >
                        {selected && (
                          <Check className="h-3 w-3 text-primary-foreground m-0.5" />
                        )}
                      </div>
                      <span 
                        className="flex-1 break-words" 
                        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                        title={opt.label}
                      >
                        {opt.label}
                      </span>
                      {selected && (
                        <Check className="ml-auto h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  {enableSearch && debouncedSearch ? "No results found" : "No options available"}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PostAnalysisMultiSelectDropdown; 